import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://riverside.com/pricing";
const MAGIC_CLIPS = "https://riverside.com/magic-clips";
const CHECKED = "2026-10-08";

export const RIVERSIDE: CompetitorRecord = {
  slug: "riverside-magic-clips",
  name: "Riverside Magic Clips",
  aliases: ["Riverside", "riverside.fm", "riverside.com", "Magic Clips", "Riverside.fm Magic Clips"],
  url: "https://riverside.com",
  pricingUrl: PRICING,
  category: "podcast",
  positioning:
    "Remote 4K multitrack recording studio with an AI editor; Magic Clips picks a recording's highlights automatically.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "2 hours of multitrack recording once, unlimited single-track recording",
      includes: [
        "Up to 720p video",
        "Riverside watermark on exports",
        "44.1 kHz audio",
        "Magic Clips included",
        "Unlimited single-track recording and editing",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The 2 multitrack hours are a one-off allowance, not monthly.",
    },
    {
      name: "Pro",
      monthly: 29,
      annualPerMonth: 24,
      annualTotal: 288,
      metered: "15 hours of separate-track downloads per month plus 2,000 AI credits",
      includes: [
        "Up to 4K video, no watermark",
        "1 studio, 48 kHz audio",
        "Unlimited transcription and text-based editing",
        "Magic Clips and show notes",
        "Podcast hosting and analytics",
        "Live Studio included",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Grow",
      monthly: 39,
      annualPerMonth: 34,
      annualTotal: 408,
      metered: "20 hours of separate-track downloads per month plus AI credits",
      includes: [
        "2 studios",
        "Full HD 1080p live streaming",
        "Hosting for 2 shows",
        "Everything in Pro",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Webinar",
      monthly: 99,
      annualPerMonth: 79,
      metered: "25 hours of separate-track downloads per month plus AI credits",
      includes: [
        "3 studios",
        "Up to 100 registrants",
        "No time limits",
        "Everything in Grow",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Annual total not listed: the page's FAQ says $99 to $79 per month, but the plan line repeats Grow's $408 annual figure, which looks like a copy error.",
    },
    {
      name: "Business",
      custom: true,
      metered: "Unlimited separate-track downloads plus 12,000 AI credits",
      includes: [
        "Unlimited studios",
        "Up to 10,000 registrants",
        "Collaborative editing and custom roles",
        "Contact sales",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Pro",
  entryPlanIncludes:
    "15 hours of separate-track downloads a month, 4K, no watermark, 2,000 AI credits, Magic Clips, show notes, hosting and analytics",
  freeTier:
    "Free plan: 2 hours of multitrack recording once, unlimited single-track, 720p with a watermark, Magic Clips included",
  trial: "No separate trial is published; the Free plan stands in",
  meteringUnit:
    "Separate-track download hours per month plus AI credits for translation, AI B-roll and animated clips; Magic Clips need no credits",
  exportResolution: "720p on Free, up to 4K on Pro and above",
  features: {
    longToShort: { v: "yes", note: "Magic Clips, on Riverside recordings or uploads" },
    momentRanking: { v: "no", note: "No score or ranking shown" },
    reframing: { v: "partial", note: "Active-speaker layouts in multicam recordings" },
    maxSource: { v: "n/a", note: "Not stated on pricing or Magic Clips pages" },
    layouts: { v: "text", note: "Vertical or square, focus speaker or keywords" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no" },
    voiceover: { v: "n/a", note: "Not found on the pricing or feature pages" },
    voiceCloning: { v: "n/a" },
    avatars: { v: "n/a" },
    timelineEditor: { v: "yes", note: "Editing suite plus text-based editing, Free included" },
    captionStyles: { v: "partial", note: "Font, color, size, placement controls; no count" },
    captionLanguages: { v: "text", note: "100+" },
    bRoll: { v: "yes", note: "AI B-roll spends AI credits" },
    fourK: { v: "yes", note: "Pro and above; Free is 720p" },
    scheduling: { v: "partial", note: "Social Scheduling listed, not tied to Magic Clips" },
    analytics: { v: "partial", note: "Podcast hosting analytics on Pro and above" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "Zapier integration only" },
    webhooks: { v: "n/a", note: "Not published" },
    web: { v: "yes" },
    desktop: { v: "n/a", note: "Not stated on the fetched pages" },
    mobile: { v: "yes", note: "iOS and Android recording apps" },
    teamSeats: { v: "partial", note: "Editor seats are a paid add-on" },
    freeTier: { v: "yes", note: "720p, watermark, Magic Clips included" },
    trial: { v: "n/a", note: "Not published; the Free plan stands in" },
    metering: { v: "text", note: "Download hours plus AI credits" },
    rollover: { v: "no", note: "Download hours do not roll over" },
    refunds: { v: "n/a", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "Magic Clips AI picks mediocre shows more often than great ones",
      date: "2026-10-01",
      url: "https://www.creatorstackclub.com/software/riverside",
      evidences: "momentRanking",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: `Pricing page fetched directly on Oct 8, 2026. The official page served a staging banner saying the information may not be up to date and its plan cards rendered blank, so every figure (Free $0, Pro $29/mo or $24/mo billed $288 a year, Grow $39/mo or $34/mo billed $408 a year, Webinar $99/mo or $79/mo, Business custom with 12,000 credits, download-hour caps of 15/20/25) was read from the page's compare table and FAQ and cross-checked against the Oct 1, 2026 creatorstackclub review. The Webinar annual total is omitted because the page's plan line repeats Grow's $408 figure while its FAQ says $79 a month. A human browser check of the live plan cards is still needed. Magic Clips scope (${MAGIC_CLIPS}) and the Free plan's Magic Clips cap were read from Riverside's own pages; no numeric Magic Clips cap is published there.`,
  },
  seoPattern: "riverside.com/riverside-vs-<x> and /blog/<x>-alternatives, no page about Crayo",
};
